// Paying for an order by card through Yoco (TAS-458), shared by the website
// and the game. Moved from apps/web/src/lib/payment.js, which keeps the
// website's own wording and re-exports everything here, so its imports and
// tests are unchanged.
//
// Once the order exists, the surface asks the yoco-checkout edge function for
// a Yoco checkout and sends the buyer to Yoco's hosted page. Yoco's signed
// webhook marks the order paid; the buyer comes back to
// <origin>/?order=…&pay=… and the surface reads the order's state from the
// server (order_payment_details, migration 265), looked up with the order
// number and the order's random idempotency key. The card is never typed into
// our pages, so no Yoco key is in any bundle either.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { supabase } from '@tasted/utils/supabase';

/** The payment method create_order saves for a card order. */
export const YOCO_METHOD = 'yoco';

/**
 * How long one request to the server may take before it counts as failed. A
 * request that never settled used to hold the checkout's 'opening' lock for
 * good, with no way to try again (TAS-463 Codex review). An object so tests
 * can shorten it.
 */
export const requestTimeout = { ms: 15000 };

function failure(name, message) {
  const err = new Error(message);
  err.name = name;
  return err;
}

/**
 * What `run(signal)` resolves to, unless `requestTimeout.ms` passes first
 * (rejects with a TimeoutError) or `outer` is aborted (an AbortError). Either
 * way `signal` is aborted, and whatever the request answers later is dropped.
 */
export function bounded(run, outer) {
  const ctrl = new AbortController();
  return new Promise((resolve, reject) => {
    if (outer?.aborted) {
      reject(failure('AbortError', 'cancelled'));
      return;
    }
    let timer;
    const end = (settle, value) => {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onAbort);
      settle(value);
    };
    const fail = (err) => {
      ctrl.abort();
      end(reject, err);
    };
    function onAbort() { fail(failure('AbortError', 'cancelled')); }
    outer?.addEventListener('abort', onAbort);
    timer = setTimeout(() => fail(failure('TimeoutError', 'timed out')), requestTimeout.ms);
    let request;
    try {
      request = run(ctrl.signal);
    } catch (err) {
      request = Promise.reject(err);
    }
    Promise.resolve(request).then((v) => end(resolve, v), (err) => end(reject, err));
  });
}

/** supabase.rpc(fn, args), bounded as above; `signal` gives up on it. */
export function boundedRpc(fn, args, signal) {
  return bounded((s) => {
    const query = supabase.rpc(fn, args);
    return typeof query?.abortSignal === 'function' ? query.abortSignal(s) : query;
  }, signal);
}

/** The order's saved payment details, read from the server. `signal` gives up on it. */
async function fetchDetails(orderNo, orderKey, signal) {
  const { data, error } = await boundedRpc('order_payment_details', { p_order_number: orderNo, p_key: orderKey }, signal);
  if (error || !data) throw error || new Error('order not found');
  return data;
}

/** Waits between lookup attempts. An object so tests can shorten them. */
export const lookupRetry = { ms: [1500, 3000] };

/**
 * The order's saved payment details, fetched once the order exists. A failed
 * fetch, or one that takes longer than requestTimeout.ms, is tried twice more
 * before it shows as an error; `reload` fetches again
 * (after an error, or once something may have changed, like a payment landing).
 * status: 'idle' (no order yet) | 'loading' | 'ready' | 'error'.
 */
export function useOrderPaymentDetails(orderNo, orderKey, { retryMs = lookupRetry.ms } = {}) {
  const [state, setState] = useState({ key: null, status: 'idle', details: null });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const key = orderNo && orderKey ? `${orderNo}|${orderKey}` : null;
  const waits = retryMs.join(',');
  useEffect(() => {
    if (!key) return undefined;
    let live = true;
    let timer;
    const gone = new AbortController();
    const delays = waits ? waits.split(',').map(Number) : [];
    setState((prev) => (prev.key === key && prev.details ? prev : { key, status: 'loading', details: null }));
    const attempt = (n) => fetchDetails(orderNo, orderKey, gone.signal).then(
      (details) => live && setState({ key, status: 'ready', details }),
      () => {
        if (!live) return;
        if (n < delays.length) timer = setTimeout(() => attempt(n + 1), delays[n]);
        else setState({ key, status: 'error', details: null });
      },
    );
    attempt(0);
    return () => { live = false; clearTimeout(timer); gone.abort(); };
  }, [key, orderNo, orderKey, nonce, waits]);
  const current = state.key === key ? state : { key, status: key ? 'loading' : 'idle', details: null };
  return { ...current, reload };
}

// ── Card payment ────────────────────────────────────────────────────────────

// Which order this tab is paying for, so the page Yoco sends the buyer back to
// can look it up (the key never goes in a URL). Session-only: it dies with the tab.
const PENDING = 'tasted.cardPayment';

/**
 * Remember the order this tab is paying for. `extra` is a small plain object
 * kept alongside (the game's { gameCode }, so its return page can link back to
 * the game); read it back with readPendingPayment.
 */
export function rememberPending(orderNo, orderKey, extra) {
  const entry = { orderNo, orderKey };
  if (extra && typeof extra === 'object') entry.extra = extra;
  try { sessionStorage.setItem(PENDING, JSON.stringify(entry)); } catch { /* private mode: the return page falls back to the email */ }
}

/** Everything remembered for `orderNo`, { orderNo, orderKey, extra }, if this tab started its payment; else null. */
export function readPendingPayment(orderNo) {
  try {
    const p = JSON.parse(sessionStorage.getItem(PENDING) || 'null');
    if (!p || p.orderNo !== orderNo || typeof p.orderKey !== 'string') return null;
    const extra = p.extra && typeof p.extra === 'object' ? p.extra : null;
    return { orderNo: p.orderNo, orderKey: p.orderKey, extra };
  } catch {
    return null;
  }
}

/** The order key for `orderNo` if this tab started its payment, else null. */
export function readPending(orderNo) {
  return readPendingPayment(orderNo)?.orderKey ?? null;
}

export function forgetPending() {
  try { sessionStorage.removeItem(PENDING); } catch { /* nothing to forget */ }
}

/** Leaving for Yoco's page. An object so tests can swap it (jsdom can't navigate). */
export const nav = { go: (url) => window.location.assign(url) };

/**
 * Open a Yoco checkout for the order and send the browser to it. `fresh` asks
 * for a new page (after a cancelled or failed one); otherwise a page opened in
 * the last few minutes is handed back. `extra` is remembered with the order
 * (see rememberPending); it is not sent anywhere. Aborting `signal` cancels the
 * attempt, as does the edge function taking longer than requestTimeout.ms:
 * either way it rejects, and it never leaves for Yoco afterwards, whatever the
 * edge function answers later (TAS-463 Codex review: a retry the buyer had
 * walked away from still took them to Yoco).
 * Resolves 'redirecting' | 'paid' | 'nothing_to_pay'; rejects if it can't start.
 */
export async function startCardPayment(orderNo, orderKey, { fresh = false, extra, signal } = {}) {
  rememberPending(orderNo, orderKey, extra);
  // The request itself isn't aborted: one that reached the server is done
  // there anyway, and only its answer is dropped.
  const { data, error } = await bounded(() => supabase.functions.invoke('yoco-checkout', {
    body: { order_number: orderNo, key: orderKey, return_to: window.location.origin, ...(fresh ? { fresh: true } : {}) },
  }), signal);
  if (error) throw error;
  if (data?.paid) return 'paid';
  if (data?.nothing_to_pay) return 'nothing_to_pay';
  if (typeof data?.redirectUrl !== 'string' || !/^https:\/\/([a-z0-9-]+\.)*yoco\.com\//i.test(data.redirectUrl)) {
    throw new Error('no Yoco checkout');
  }
  if (signal?.aborted) throw failure('AbortError', 'cancelled');
  nav.go(data.redirectUrl);
  return 'redirecting';
}

/**
 * Where a placed order's payment stands, for the checkout around it
 * (useCommerce's `orderPayment`):
 *   'opening'  the order is saved and its payment is on its way to Yoco: the
 *              details are loading, or Yoco's page is being opened. Nothing
 *              may close the checkout, or the buyer could order again while
 *              the first order can still send them to Yoco (TAS-463 Codex review).
 *   'due'      the order still owes money and nothing is under way: the
 *              details or Yoco's page couldn't be fetched, or the buyer came
 *              back from Yoco's page. The checkout may close, but the order is
 *              kept, and coming back to the checkout comes back to it.
 *   null       no order, or nothing left to pay on it.
 */
export function orderPaymentState(orderNo, lookup, card) {
  if (!orderNo) return null;
  if (lookup?.status === 'error') return 'due';
  const d = lookup?.status === 'ready' ? lookup.details : null;
  if (!d) return 'opening';
  if (d.paid || Number(d.total) <= 0 || d.payment_method !== YOCO_METHOD) return null;
  return card?.status === 'error' ? 'due' : 'opening';
}

/**
 * Starts the card payment by itself once a card order is known to be unpaid,
 * once per order; `retry` starts it again after a failure, asking Yoco for a
 * fresh page for the same order. If the server says
 * the order is already paid (or owes nothing), the details are read again;
 * should they still say unpaid, it falls back to 'error' and a retry.
 * `extra` is remembered with the order, as in startCardPayment.
 * Only the latest attempt counts: one replaced by a retry, or still under way
 * when the checkout unmounts (the buyer went Back, or to another page), is
 * cancelled, and never changes the status or leaves for Yoco (TAS-463 Codex
 * review, round 3).
 * status: 'idle' | 'starting' | 'redirecting' | 'checking' | 'error'
 */
export function useCardPayment(orderNo, orderKey, lookup, { onStart, extra } = {}) {
  const [status, setStatus] = useState('idle');
  const started = useRef(null);
  const d = lookup?.status === 'ready' ? lookup.details : null;
  const due = !!(orderNo && orderKey && d && d.payment_method === YOCO_METHOD && !d.paid && Number(d.total) > 0);

  // Read when the payment starts, so a fresh object each render doesn't
  // restart anything.
  const extraRef = useRef(extra);
  extraRef.current = extra;

  // The attempt under way, { ctrl, fresh }, until it settles; and one the
  // unmount cancelled, for a remount to pick up again (see below).
  const attempt = useRef(null);
  const interrupted = useRef(null);

  const reload = lookup?.reload;
  const checkedFrom = useRef(null);
  const launch = useCallback((fresh) => {
    attempt.current?.ctrl.abort();
    const mine = { ctrl: new AbortController(), fresh };
    attempt.current = mine;
    // False once this attempt was replaced or cancelled: its answer is dropped.
    const current = () => {
      if (attempt.current !== mine) return false;
      attempt.current = null;
      return true;
    };
    const opts = { ...(fresh ? { fresh: true } : {}), ...(extraRef.current ? { extra: extraRef.current } : {}), signal: mine.ctrl.signal };
    startCardPayment(orderNo, orderKey, opts).then(
      (result) => {
        if (!current()) return undefined;
        if (result === 'redirecting') return setStatus('redirecting');
        // Paid (or nothing owed) since the details were read: read them again.
        checkedFrom.current = d;
        setStatus('checking');
        return reload?.();
      },
      () => { if (current()) setStatus('error'); },
    );
  }, [orderNo, orderKey, reload, d]);

  const start = useCallback((fresh = false) => {
    if (!orderNo || !orderKey) return;
    setStatus('starting');
    onStart?.();
    launch(fresh);
  }, [onStart, launch, orderNo, orderKey]);

  // Cancel before paint when the order changes or the checkout unmounts.
  // StrictMode replays effects for the same order: resume only that attempt,
  // without counting another start or requesting a fresh Yoco page.
  const launchRef = useRef(launch);
  launchRef.current = launch;
  useLayoutEffect(() => {
    const resume = interrupted.current;
    interrupted.current = null;
    if (resume && resume.orderNo === orderNo && resume.orderKey === orderKey) {
      launchRef.current(resume.fresh);
    } else {
      started.current = null;
      checkedFrom.current = null;
      setStatus('idle');
    }
    return () => {
      const live = attempt.current;
      attempt.current = null;
      if (!live) return;
      live.ctrl.abort();
      interrupted.current = { ...live, orderNo, orderKey };
    };
  }, [orderNo, orderKey]);

  useEffect(() => {
    if (status === 'checking' && d && d !== checkedFrom.current && due) setStatus('error');
  }, [status, d, due]);

  useEffect(() => {
    if (!due || started.current === orderNo) return;
    started.current = orderNo;
    start();
  }, [due, orderNo, start]);

  // Back from Yoco's page through history: the browser can show this page
  // again from its cache, exactly as it left, still "redirecting". Nothing is
  // under way any more, so offer the payment again instead of a page that
  // waits forever.
  useEffect(() => {
    if (status !== 'redirecting' || typeof window === 'undefined') return undefined;
    const onShow = (event) => { if (event.persisted) setStatus('error'); };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, [status]);

  // Retrying is resuming the same order: a fresh page, never a new order.
  const retry = useCallback(() => start(true), [start]);

  return { status: due ? status : 'idle', retry };
}

// ── Back from Yoco ──────────────────────────────────────────────────────────

const ORDER_NO = /^[A-Z]{2,6}-[0-9A-Z]{3,10}$/;

/** `?order=TST-12345&pay=success|cancelled|failed`, or null if this isn't a return from Yoco. */
export function readPaymentReturn(search = window.location.search) {
  const q = new URLSearchParams(search);
  const orderNo = (q.get('order') || '').trim();
  const result = q.get('pay');
  if (!ORDER_NO.test(orderNo) || !['success', 'cancelled', 'failed'].includes(result)) return null;
  return { orderNo, result };
}

/**
 * After Yoco sends the buyer back: the order's state, read from the server
 * (the URL proves nothing). On "success" it asks again for a short while,
 * because Yoco's webhook can land a few seconds after the buyer does.
 * `extra` is what was remembered with the order (see rememberPending), or null.
 * status: 'checking' | 'paid' | 'unpaid' | 'unknown' (no key in this tab, or the lookup failed or timed out)
 */
export function usePaymentReturn(ret, { tries = 10, every = 2500 } = {}) {
  const pending = ret ? readPendingPayment(ret.orderNo) : null;
  const orderKey = pending?.orderKey ?? null;
  const extra = pending?.extra ?? null;
  const [state, setState] = useState({ status: ret && orderKey ? 'checking' : 'unknown', details: null });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  useEffect(() => {
    if (!ret || !orderKey) return undefined;
    let live = true;
    let timer;
    const gone = new AbortController();
    setState((prev) => (prev.status === 'unknown' ? { status: 'checking', details: null } : prev));
    const attempt = async (n) => {
      try {
        const d = await fetchDetails(ret.orderNo, orderKey, gone.signal);
        if (!live) return;
        if (d.paid) return setState({ status: 'paid', details: d });
        if (ret.result === 'success' && n + 1 < tries) {
          timer = setTimeout(() => attempt(n + 1), every);
          return undefined;
        }
        return setState({ status: 'unpaid', details: d });
      } catch {
        if (live) setState({ status: 'unknown', details: null });
      }
      return undefined;
    };
    attempt(0);
    return () => { live = false; clearTimeout(timer); gone.abort(); };
  }, [ret, orderKey, tries, every, nonce]);
  return { ...state, orderKey, extra, reload };
}
